import { useEffect, useMemo, useState } from "react";
import { Trophy, Target, Flame, Sparkles } from "lucide-react";

/**
 * Feb-2026 — Highlights des Monats für Vermittler.
 *
 * Drei Kennzahlen aus den vorhandenen Daten:
 *  🏆 Punkte-Best-Ever — höchste je erhaltene Punkt-Zahl in einer Aktion
 *  🎯 Konvertierungs-Rate — Empfehlungen → erfolgreiche Verkäufe (%)
 *  🔥 Top-Tipp — Aktion mit der höchsten Einzel-Auszahlung
 *
 * Erkennt neue Rekorde (Vergleich zum letzten Render-Snapshot in
 * localStorage) und animiert die jeweilige Pille kurz.
 */
export default function HighlightsOfMonthCard({ activities = [], totals = {} }) {
    const stats = useMemo(() => {
        // Award-Aktionen mit Punkten
        const awards = activities.filter((a) => a && (a.points || 0) > 0);
        const bestEver = awards.reduce((m, a) => Math.max(m, a.points || 0), 0);

        // Konvertierungs-Rate aus referrals + sales-Aktionstypen
        const referralCount = activities.filter((a) =>
            ["manual_referral_submitted", "smily_report_submitted", "transaction_code_use"].includes(a.action_key)
        ).length;
        const conversions = activities.filter((a) =>
            ["referral_converted", "sale_attributed", "smily_converted"].includes(a.action_key)
        ).length;
        const conversionRate = referralCount > 0
            ? Math.round((conversions / referralCount) * 100)
            : 0;

        // Top-Tipp = höchste Einzel-Auszahlung mit Label
        const topTip = awards
            .slice()
            .sort((a, b) => (b.points || 0) - (a.points || 0))[0] || null;

        return { bestEver, conversionRate, conversions, referralCount, topTip };
    }, [activities]);

    // Rekord-Detection
    const [pulseKey, setPulseKey] = useState(null);
    useEffect(() => {
        try {
            const k = "twowheels.vermittler.highlights.snapshot";
            const prev = JSON.parse(localStorage.getItem(k) || "{}");
            if (prev.bestEver != null && stats.bestEver > prev.bestEver) {
                setPulseKey("best");
                setTimeout(() => setPulseKey(null), 4000);
            } else if (prev.conversionRate != null && stats.conversionRate > prev.conversionRate && stats.referralCount > 0) {
                setPulseKey("rate");
                setTimeout(() => setPulseKey(null), 4000);
            }
            localStorage.setItem(k, JSON.stringify({
                bestEver: stats.bestEver,
                conversionRate: stats.conversionRate,
            }));
        } catch { /* ignore */ }
    }, [stats.bestEver, stats.conversionRate, stats.referralCount]);

    if (!activities.length && !totals.awarded) return null;

    return (
        <div className="bg-gradient-to-br from-[#FFCC00]/30 via-amber-50 to-white border-2 border-[#FFCC00] rounded-sm p-4" data-testid="highlights-card">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#111827]" />
                <h3 className="font-display font-black text-sm tracking-tight uppercase">Highlights des Monats</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Pill
                    icon={Trophy}
                    label="Punkte-Best-Ever"
                    value={stats.bestEver}
                    sublabel="höchste Einzel-Belohnung"
                    pulse={pulseKey === "best"}
                    testid="highlight-best-ever"
                />
                <Pill
                    icon={Target}
                    label="Konvertierungs-Rate"
                    value={`${stats.conversionRate}%`}
                    sublabel={`${stats.conversions} von ${stats.referralCount} Tipps`}
                    pulse={pulseKey === "rate"}
                    testid="highlight-conversion-rate"
                />
                <Pill
                    icon={Flame}
                    label="Top-Tipp"
                    value={stats.topTip ? stats.topTip.points : 0}
                    sublabel={stats.topTip?.label?.slice(0, 32) || "noch kein Tipp"}
                    testid="highlight-top-tip"
                />
            </div>
        </div>
    );
}

function Pill({ icon: Icon, label, value, sublabel, pulse, testid }) {
    return (
        <div
            className={`bg-white border rounded-sm p-3 ${pulse ? "border-[#FFCC00] ring-4 ring-[#FFCC00]/40 animate-pulse" : "border-gray-200"}`}
            data-testid={testid}
        >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-600 mb-1">
                <Icon className="w-3.5 h-3.5 text-[#111827]" />
                {label}
            </div>
            <div className="font-display font-black text-2xl font-mono-num text-[#111827] leading-none mb-1">
                {value}
            </div>
            <div className="text-[10px] text-gray-500 truncate" title={sublabel}>{sublabel}</div>
            {pulse && (
                <div className="text-[9px] uppercase tracking-wider font-bold text-[#FFCC00] mt-1">🎉 Neuer Rekord!</div>
            )}
        </div>
    );
}
